import { useEffect, useMemo, useRef, useState } from 'react';
import {
  BIOMES,
  WORLD_NODES,
  isSlotNodeType,
  isSlotUnlocked,
  type WorldMapNode,
} from '@goodtime/shared';
import { AudioBus } from '@goodtime/engine';
import { LOBBY_MUSIC } from '../../../themes';
import { Avatar } from '../Avatar';
import { NodePreview } from '../NodePreview';
import { WheelNodePreview } from '../WheelNodePreview';
import { MapLayerBackground } from './MapLayerBackground';
import { MapLayerMid } from './MapLayerMid';
import { MapNodePlatform } from './MapNodePlatform';
import { MapHud } from './MapHud';
import { BiomeForegroundEffects } from './effects/BiomeForegroundEffects';
import { DailyQuestClaimToast } from './DailyQuestClaimToast';

// Idle-motion keyframes (T7). Effects are CSS-only and anchored to
// their parallax layer so they scroll with the asset they belong to,
// not the viewport. Names follow the gt* convention used elsewhere.
const ANIMATION_KEYFRAMES = `
@keyframes gtFloat { 0%,100% { transform: translate(-50%, 0); } 50% { transform: translate(-50%, -4px); } }
@keyframes gtIdle { 0%,100% { transform: translate(-50%, -100%); } 50% { transform: translate(-50%, calc(-100% - 3px)); } }
@keyframes gtPulse { 0% { box-shadow: 0 0 14px 2px rgba(255,225,130,0.65), 0 0 28px 4px rgba(255,201,64,0.35); } 50% { box-shadow: 0 0 30px 12px rgba(255,225,130,0.95), 0 0 56px 18px rgba(255,201,64,0.55); } 100% { box-shadow: 0 0 14px 2px rgba(255,225,130,0.65), 0 0 28px 4px rgba(255,201,64,0.35); } }
@keyframes gtFadeIn { 0% { opacity: 0; } 100% { opacity: 1; } }
@keyframes gtSmoke {
  0%   { transform: translate(0, 0) scale(0.55); opacity: 0; }
  10%  { opacity: 0.9; }
  60%  { opacity: 0.55; }
  100% { transform: translate(-30%, -380%) scale(1.6); opacity: 0; }
}
@keyframes gtFlicker {
  0%, 100%  { opacity: 0.55; }
  18%       { opacity: 0.95; }
  19%       { opacity: 0.4; }
  21%       { opacity: 0.92; }
  60%       { opacity: 0.78; }
  62%       { opacity: 0.5; }
  64%       { opacity: 0.85; }
}
@keyframes gtCanopy {
  0%, 100% { opacity: 0.55; transform: translateX(0); }
  50%      { opacity: 0.9; transform: translateX(8px); }
}
@keyframes gtBlink {
  0%, 92%, 100% { opacity: 0; }
  93%, 96%      { opacity: 0.85; }
}
@keyframes gtWingFlap {
  0%, 100% { transform: scaleX(1); }
  50%      { transform: scaleX(0.25); }
}
@keyframes gtFlutterA {
  0%   { transform: translate(0, 0); }
  25%  { transform: translate(40px, -22px); }
  50%  { transform: translate(80px, 6px); }
  75%  { transform: translate(40px, 24px); }
  100% { transform: translate(0, 0); }
}
@keyframes gtFlutterB {
  0%   { transform: translate(0, 0); }
  25%  { transform: translate(-50px, 18px); }
  50%  { transform: translate(-90px, -10px); }
  75%  { transform: translate(-50px, -28px); }
  100% { transform: translate(0, 0); }
}
@keyframes gtBeam {
  0%   { transform: translate(-50%, 0) rotate(0deg); }
  100% { transform: translate(-50%, 0) rotate(360deg); }
}
@keyframes gtBirdDrift {
  0%   { transform: translate(-60px, 0) rotate(0deg); }
  12%  { transform: translate(360px, -32px) rotate(-3deg); }
  25%  { transform: translate(750px, -44px) rotate(2deg); }
  38%  { transform: translate(1140px, -8px) rotate(4deg); }
  52%  { transform: translate(1560px, 26px) rotate(2deg); }
  65%  { transform: translate(1950px, 18px) rotate(-1deg); }
  78%  { transform: translate(2340px, -22px) rotate(-3deg); }
  90%  { transform: translate(2700px, -38px) rotate(-1deg); }
  100% { transform: translate(3000px, -12px) rotate(0deg); }
}
`;

/** How many nodes ahead of {@code currentNodeId} stay unlocked.
 *  Bumped from 6 → 12 on 2026-05-12 when the second batch of biome
 *  nodes (royal_reels / dragon_reels / knights_reels / wizard_reels)
 *  pushed wheel + boss nodes further along the biome row. Existing
 *  player profiles with a stale {@code currentNodeId} were silently
 *  locked out of the multiplayer Forest Wheel entry — clicking the
 *  node platform fell through to a no-op (the click handler bails
 *  on locked nodes). Wheel nodes also bypass the lock entirely
 *  below — see {@link MapNodePlatform}'s onClick wiring. */
const NODES_VISIBLE_AHEAD = 12;

/** Pixel width per node along the L1 strip. The full biome (8 nodes)
 * spans roughly NODE_PITCH × 8 plus margins. Tuned for a sense of
 * decent horizontal travel. */
const NODE_PITCH = 300;
/** Left/right padding before first/after-last node. */
const HORIZONTAL_PAD = 220;
/** Pixel height of the content strip (matches viewport min-height). */
const STRIP_HEIGHT = 720;
/** Where the L1 nodes sit vertically. Anchored above the painted
 * ground line of the L2 panorama. */
const NODE_BOTTOM_PX = 80;

const PARALLAX_BG = 0.25;
const PARALLAX_MG = 0.5;

export type TimeOfDay = 'day' | 'night';

export interface WorldMapV2Props {
  /** Player's current node id (1-indexed; 1..40). */
  currentNodeId: number;
  /** Set of completed node ids. */
  completed: ReadonlySet<number>;
  /** Index 0..4 of the biome currently being viewed. */
  currentBiomeIdx: number;
  /** Player taps Play in the preview modal. */
  onPlayNode: (node: WorldMapNode) => void;
  /** Inline-claim handler for the Daily Quest node. Producer audit
   * Phase 2.10 — tapping the Daily Quest coin should fire the claim
   * directly on the map (with a floating "+50 coins" toast and the
   * HUD counter rolling up), never sending the player into a separate
   * scene just to tap a CLAIM button. Returns (or resolves to) the
   * credited reward, or null when the daily is on cooldown, so the toast
   * can render the right copy. A rejected claim shows no toast: the host
   * reports the failure. Pass null/omit to fall back to the legacy
   * scene-switch path. */
  onClaimDailyQuest?:
    | (() =>
        | { coins: number; gems: number }
        | null
        | Promise<{ coins: number; gems: number } | null>)
    | null;
  /** Wallet display values for the static HUD. */
  coinBalance: number;
  gemBalance: number;
  /** True while the server wallet is still loading on a cold launch —
   *  forwarded to {@link MapHud} so the balance pill shows neutral
   *  placeholders rather than the local placeholder balance. */
  walletLoading?: boolean;
  /** Login streak shown as a chip on the HUD (when > 0). */
  streak: number;
  /** Player level shown as a chip on the HUD. */
  level: number;
  /** Open the daily-bonus modal (HUD gift button). Pass null to hide
   * the gift button entirely — used once today's bonus has been
   * claimed so we don't surface a CTA that resolves to "+0 coins". */
  onOpenDailyBonus?: (() => void) | null;
  /** Open the main multiplayer game from the HUD crown medallion. */
  onOpenMainGame?: () => void;
  /** Open the gem shop (tapping the gem icon in the HUD balance pill). */
  onTapGems?: () => void;
  /** Flip the app view back to the all-biomes list. This is the
   *  canonical episode-navigation surface (invoked by the EPISODES
   *  button in the {@link MapHud}); the in-map prev/next portals it
   *  replaced have since been removed. */
  onBackToBiomes?: () => void;
  /** T1 — player's purchased slot ids (stringified WorldMapNode.id).
   *  Used to mark gem-locked slot tiles with the chained-padlock
   *  visual (the tiles stay tappable — onPlayNode routes into
   *  SlotUnlockSheet). Optional; treated as empty when missing. */
  unlockedSlotIds?: ReadonlyArray<string>;
  /** M1 slot mastery — world-node id → stars earned (0–3). Drives the
   *  bronze/silver/gold medal on each slot tile. Optional; tiles with no
   *  entry render no medal (0 stars). */
  slotStars?: ReadonlyMap<number, number>;
  /** Tap the player avatar marker to open the avatar picker. When omitted the
   *  avatar stays a passive (non-interactive) marker. */
  onOpenAvatarPicker?: () => void;
  /** Day/night atmosphere (currently unused in V2 — sky comes from the
   * commissioned panorama; reserved for future). */
  timeOfDay?: TimeOfDay;
}

/**
 * World map V2 — horizontal-scrolling, 3-layer parallax view of a
 * SINGLE biome at a time.
 *
 * <p>Layer stack (back → front):
 * <ol>
 *   <li>L3 background — wide horizontal panorama, slowest parallax (0.25×)
 *   <li>L2 midground — wide horizontal panorama with transparent sky (0.5×)
 *   <li>L1 foreground — node platforms on a green grass strip (1.0×)
 * </ol>
 *
 * <p>Episode navigation lives in the {@link MapHud} EPISODES button
 * (→ the all-episodes list); there are no in-map biome portals.
 *
 * <p>A {@link MapHud} static overlay sits on top with the title card,
 * coin+gem balance, daily-bonus gift, streak/level chips, and main
 * game crown badge.
 *
 * <p>Reference: PvZ Pirate Seas (per user direction). The mockup uses
 * three independently-scrolling parallax layers for depth.
 */
export function WorldMapV2({
  currentNodeId,
  completed,
  currentBiomeIdx,
  onPlayNode,
  onClaimDailyQuest = null,
  coinBalance,
  gemBalance,
  walletLoading = false,
  streak,
  level,
  onOpenDailyBonus,
  onOpenMainGame,
  onTapGems,
  onBackToBiomes,
  unlockedSlotIds,
  slotStars,
  onOpenAvatarPicker,
}: WorldMapV2Props) {
  const [selectedNode, setSelectedNode] = useState<WorldMapNode | null>(null);
  const [scrollLeft, setScrollLeft] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  // Phase 2.10 — Daily Quest inline-claim toast state. Anchored at the
  // node's screen position when the player taps it; cleared by the
  // toast's onDone callback after the rise + fade finishes.
  const [dailyClaimToast, setDailyClaimToast] = useState<{
    reward: { coins: number; gems: number } | null;
    success: boolean;
    anchorX: number;
    anchorY: number;
  } | null>(null);

  const biome = BIOMES[currentBiomeIdx];
  if (!biome) {
    return null;
  }
  // Filter the 8 nodes belonging to this biome and sort by ID so
  // left-to-right order across the L1 strip matches the playthrough
  // order (1, 2, 3, …, 8) — not the snake-pattern x positions used
  // by V1's vertical map.
  const biomeNodes = useMemo(
    () =>
      WORLD_NODES.filter((n) => n.biome === currentBiomeIdx).sort(
        (a, b) => a.id - b.id,
      ),
    [currentBiomeIdx],
  );

  // Episode nav is the EPISODES button (→ all-episodes list); the
  // in-map prev/next portals were retired, so just pad both ends.
  const firstNodeX = HORIZONTAL_PAD;
  // Nodes are centre-anchored at firstNodeX + i*NODE_PITCH, so the LAST one
  // sits at (length-1)*NODE_PITCH. The old formula used length*NODE_PITCH,
  // which left a whole extra pitch (~300px) of empty panorama after the
  // final node that you could scroll into — the "big gap at the end of each
  // biome" (producer 2026-06-10). Drop to (length-1) so the trailing space
  // is just HORIZONTAL_PAD: enough to centre the last node on a phone
  // without an over-scroll void.
  const worldWidth =
    firstNodeX +
    Math.max(0, biomeNodes.length - 1) * NODE_PITCH +
    HORIZONTAL_PAD;

  // When the biome changes, scroll back to the start.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = 0;
      setScrollLeft(0);
    }
  }, [currentBiomeIdx]);

  // Lobby music — lazy-load the buffer on first map-view mount, then
  // start it. Stage 1 of the install-size project: lobby music isn't
  // in SHARED_AUDIO, so loading it here means a fresh install that
  // boots straight to the slot scene never fetches the lobby track.
  // playMusic queues if the context is still suspended OR if the
  // buffer hasn't decoded yet — both unblock automatically once their
  // gates open. No stop on unmount: SlotDemoScene's playMusic call
  // cross-fades to the biome track; returning to the map remounts and
  // cross-fades back.
  useEffect(() => {
    void AudioBus.shared.loadEntry(LOBBY_MUSIC.alias, LOBBY_MUSIC.src);
    AudioBus.shared.playMusic(LOBBY_MUSIC.alias, { fadeInMs: 500 });
  }, []);

  const previewBiome = selectedNode ? (BIOMES[selectedNode.biome] ?? null) : null;

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        background: biome.sky[2],
        fontFamily: '"Fredoka", -apple-system, system-ui',
      }}
    >
      <style>{ANIMATION_KEYFRAMES}</style>

      {/* L3 background — fixed in viewport, parallax via translateX */}
      <MapLayerBackground
        biome={biome}
        scrollLeft={scrollLeft}
        parallax={PARALLAX_BG}
      />

      {/* L2 midground — fixed in viewport, parallax via translateX */}
      <MapLayerMid
        biome={biome}
        scrollLeft={scrollLeft}
        parallax={PARALLAX_MG}
      />

      {/* L1 foreground — horizontally scrollable container holds the
          node platforms + portal at the right edge.
          touch-action: pan-x explicitly hands horizontal touch
          gestures to the scroller (otherwise a touch that starts on a
          node <button> can be claimed by the click handler and the
          drag-to-scroll never engages). */}
      <div
        ref={scrollRef}
        onScroll={(e) => {
          // Clamp to [0, maxScroll] before feeding the parallax layers.
          // iOS elastic overscroll reports a NEGATIVE scrollLeft at the
          // left edge (and past-max at the right) during the rubber-band
          // bounce; without clamping, the L2/L3 panoramas translate past
          // their bounds and the stacked biome layers behind them peek
          // out. Clamping pins the top image's X at 0 (start) / max
          // (end) so it can never slide past the edge. Producer
          // 2026-05-29.
          const el = e.currentTarget as HTMLDivElement;
          const max = el.scrollWidth - el.clientWidth;
          setScrollLeft(Math.max(0, Math.min(el.scrollLeft, max)));
        }}
        onMouseDown={(e) => {
          // Mouse drag-to-scroll for desktop + Chrome devtools mobile
          // preview (which doesn't faithfully simulate touch panning).
          // Skip when the mousedown is on a button/anchor — those need
          // their click handler. The check uses closest() so any
          // descendant of a button (image, span, …) is also exempt.
          const target = e.target as HTMLElement;
          if (target.closest('button, a')) return;
          const el = scrollRef.current;
          if (!el) return;
          const startX = e.clientX;
          const startScroll = el.scrollLeft;
          const onMove = (ev: MouseEvent) => {
            el.scrollLeft = startScroll - (ev.clientX - startX);
          };
          const onUp = () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
          };
          window.addEventListener('mousemove', onMove);
          window.addEventListener('mouseup', onUp);
        }}
        style={{
          position: 'absolute',
          inset: 0,
          overflowX: 'auto',
          overflowY: 'hidden',
          WebkitOverflowScrolling: 'touch',
          // Kill the rubber-band bounce at the horizontal edges so the
          // foreground track can't elastic-pull past the panorama and
          // expose the stacked layers behind it. Pairs with the
          // scrollLeft clamp above (which pins the parallax layers).
          overscrollBehaviorX: 'contain',
          touchAction: 'pan-x',
          scrollbarWidth: 'none',
          cursor: 'grab',
          zIndex: 2,
        }}
      >
        <div
          style={{
            position: 'relative',
            width: worldWidth,
            height: '100%',
            minHeight: STRIP_HEIGHT,
          }}
        >
          {/* L1 floor — node platforms sit directly on the painted
              ground line of the L2 panorama (which extends across the
              full world width). The procedural grass strip that lived
              here was V1 chrome from when L2 was a small vignette;
              with the wide commissioned L2 it became a green wall
              behind the nodes. Removed entirely. */}

          {/* L1 idle effects (butterflies, tumbleweed, etc.) — sit
              behind the nodes/portals so the foreground UI is never
              occluded. Re-keyed on biome so effects fade in fresh on
              biome change. */}
          <BiomeForegroundEffects
            key={biome.id}
            biomeId={biome.id}
            worldWidth={worldWidth}
          />

          {/* Node platforms */}
          {biomeNodes.map((n, i) => {
            const leftPx = firstNodeX + i * NODE_PITCH;
            return (
              <MapNodePlatform
                key={n.id}
                node={n}
                leftPx={leftPx}
                bottomPx={NODE_BOTTOM_PX}
                isCurrent={n.id === currentNodeId}
                isCompleted={completed.has(n.id)}
                stars={slotStars?.get(n.id) ?? 0}
                isLocked={
                  // Wheel + slot nodes are ALWAYS tappable (isLocked
                  // governs both visual AND click). The gem-economy
                  // visual lock for slots is carried separately on
                  // {@code isGemLocked} so the tile still routes
                  // into {@code SlotUnlockSheet}. {@code isSlotNodeType}
                  // covers every themed-slot variant (royal_reels,
                  // dragon_reels, …) so any slot can be purchased
                  // directly without playing prior slots in the row
                  // (producer brief 2026-06-06 — "it should be
                  // possible to purchase any slot with gems").
                  n.type !== 'wheel' &&
                  !isSlotNodeType(n.type) &&
                  (n.id > currentNodeId + NODES_VISIBLE_AHEAD ||
                    n.type === 'locked')
                }
                isGemLocked={
                  // T1 (2026-06-04 producer brief) — slot tiles that
                  // the player hasn't bought yet render with the
                  // chained-padlock icon AND stay tappable.
                  // BiomeListScreen was already drawing them locked
                  // in the all-episodes view; this brings WorldMapV2
                  // into visual parity so a player who taps in sees
                  // the same lock state both places.
                  isSlotNodeType(n.type) &&
                  !isSlotUnlocked(n.id, unlockedSlotIds ?? [])
                }
                onClick={(node, event) => {
                  // Phase 2.10 — Daily Quest is a one-tap inline
                  // claim, not a scene switch. Skip the preview
                  // popup, fire the claim, render the floating
                  // toast at the click position. On cooldown the
                  // toast says "back tomorrow" instead.
                  if (node.type === 'daily' && onClaimDailyQuest) {
                    // Read the anchor now: currentTarget is gone once the
                    // claim's round trip resolves.
                    const target = event?.currentTarget as HTMLElement | undefined;
                    const rect = target?.getBoundingClientRect();
                    void Promise.resolve(onClaimDailyQuest()).then(
                      (reward) =>
                        setDailyClaimToast({
                          reward,
                          success: reward !== null,
                          anchorX: rect ? rect.left + rect.width / 2 : window.innerWidth / 2,
                          anchorY: rect ? rect.top : window.innerHeight / 2,
                        }),
                      () => undefined,
                    );
                    return;
                  }
                  setSelectedNode(node);
                }}
              />
            );
          })}

          {/* Avatar floats above the current node (if it's in this biome) */}
          {(() => {
            const idx = biomeNodes.findIndex((n) => n.id === currentNodeId);
            if (idx < 0) return null;
            const leftPx = firstNodeX + idx * NODE_PITCH;
            return (
              <div
                style={{
                  position: 'absolute',
                  left: leftPx,
                  bottom: NODE_BOTTOM_PX + 90,
                  transform: 'translate(-50%, 0)',
                  zIndex: 31,
                  // Tappable only when a picker handler is wired; otherwise the
                  // marker stays passive so it never eats map gestures.
                  pointerEvents: onOpenAvatarPicker ? 'auto' : 'none',
                  animation: 'gtIdle 2.4s ease-in-out infinite',
                  filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.45))',
                }}
              >
                <Avatar size={72} onPress={onOpenAvatarPicker} />
              </div>
            );
          })()}

        </div>
      </div>

      {/* Static HUD overlay — does not scroll */}
      <MapHud
        biome={biome}
        coinBalance={coinBalance}
        gemBalance={gemBalance}
        walletLoading={walletLoading}
        streak={streak}
        level={level}
        onDailyBonus={onOpenDailyBonus}
        onMainGame={() => {
          // Crown medallion is the second multiplayer entry point. Route
          // it through the same WheelNodePreview dialogue the wheel-node
          // tap on the L1 strip uses, so the user picks a play mode
          // (bots / friends / public) before the room is created.
          const wheelNode = biomeNodes.find((n) => n.type === 'wheel') ?? null;
          if (wheelNode) {
            setSelectedNode(wheelNode);
          } else if (onOpenMainGame) {
            // No wheel node in this biome — fall back to the App's
            // legacy direct-open path. Should not happen with current
            // WORLD_NODES (every biome has a wheel) but kept for safety.
            onOpenMainGame();
          }
        }}
        onTapGems={onTapGems}
        onBackToBiomes={onBackToBiomes ?? null}
      />

      {/* Preview modal — wheel rooms get the how-it-works dialogue;
          every other node uses the standard candy-coin preview. */}
      {selectedNode && previewBiome && selectedNode.type === 'wheel' && (
        <WheelNodePreview
          node={selectedNode}
          biome={previewBiome}
          onClose={() => setSelectedNode(null)}
          onEnter={(node) => onPlayNode(node)}
        />
      )}
      {selectedNode && previewBiome && selectedNode.type !== 'wheel' && (
        <NodePreview
          node={selectedNode}
          biome={previewBiome}
          onClose={() => setSelectedNode(null)}
          onPlay={(node) => onPlayNode(node)}
        />
      )}
      {dailyClaimToast ? (
        <DailyQuestClaimToast
          reward={dailyClaimToast.reward}
          success={dailyClaimToast.success}
          anchorX={dailyClaimToast.anchorX}
          anchorY={dailyClaimToast.anchorY}
          onDone={() => setDailyClaimToast(null)}
        />
      ) : null}
    </div>
  );
}
